import parametersSelectors from '@/pages/Parameters/parameters.selectors'
import QuestionIconWithTooltip from '@/shared/Common/QuestionIconWithTooltip'
import ThreeDotsLoader from '@/shared/Loaders/ThreeDotsLoader'
import { useRecoilValue } from 'recoil'
import css from '../liftIndicators.module.css'
import { TParameterKey } from '@/pages/Parameters/parameters.types'

interface IProps {
  values: Record<TParameterKey, number>
  loading: boolean
}

const LiftIndicatorsWidget = (props: IProps) => {
  const sourceIdToIdMap = useRecoilValue(parametersSelectors.getSourceIdToIdMap)

  return (
    <div style={{ minHeight: 0, overflow: 'auto', position: 'relative' }}>
      {props.loading && (
        <div className={css.loadingWrapper}>
          <ThreeDotsLoader inscription="Загрузка последних значений..." />
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div className="flex-lines gap16 width100">
          {/* Частотный преобразователь */}
          <div className={css.indicationBlockWrapper}>
            <div className="flex-lines gap24">
              <h5 className="text-heading-5">Частотный преобразователь</h5>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '32px' }}>
                <div className="inline-flex-gap center gap8">
                  <div className={css.indicator} data-active={Boolean(props.values[sourceIdToIdMap['FCUpCmd']])} />
                  <p className="text-nm">UP</p>
                  <QuestionIconWithTooltip text="Сигнал UP" position="right" />
                </div>
                <div className="inline-flex-gap center gap8">
                  <div className={css.indicator} data-active={Boolean(props.values[sourceIdToIdMap['FCSELTCmd']])} />
                  <p className="text-nm">SELT</p>
                  <QuestionIconWithTooltip text="Сигнал SELT" position="bottom" />
                </div>
                <div className="inline-flex-gap center gap8">
                  <div className={css.indicator} data-active={Boolean(props.values[sourceIdToIdMap['MDRunSgl']])} />
                  <p className="text-nm">RUN</p>
                  <QuestionIconWithTooltip text="Сигнал RUN" position="bottom" />
                </div>

                <div className="inline-flex-gap center gap8">
                  <div className={css.indicator} data-active={Boolean(props.values[sourceIdToIdMap['FCDownCmd']])} />
                  <p className="text-nm">DOWN</p>
                  <QuestionIconWithTooltip text="Сигнал DOWN" position="right" />
                </div>
                <div className="inline-flex-gap center gap8">
                  <div className={css.indicator} data-active={Boolean(props.values[sourceIdToIdMap['FCRSTCmd']])} />
                  <p className="text-nm">RST</p>
                  <QuestionIconWithTooltip text="Сигнал RST" position="bottom" />
                </div>
                <div className="inline-flex-gap center gap8">
                  <div
                    className={css.indicator}
                    data-active={Boolean(props.values[sourceIdToIdMap['BRCxControlFlag']])}
                  />
                  <p className="text-nm">BREAK</p>
                  <QuestionIconWithTooltip text="Контроль микровыключателей тормоза" position="bottom" />
                </div>

                <div className="inline-flex-gap center gap8">
                  <div className={css.indicator} data-active={Boolean(props.values[sourceIdToIdMap['FCHSCmd']])} />
                  <p className="text-nm">HS</p>
                  <QuestionIconWithTooltip text="Сигнал HS" position="right" />
                </div>
                <div className="inline-flex-gap center gap8">
                  <div className={css.indicator} data-active={Boolean(props.values[sourceIdToIdMap['FCBXSgl']])} />
                  <p className="text-nm">BX</p>
                  <QuestionIconWithTooltip text="Сигнал BX" position="bottom" />
                </div>
                <div className="inline-flex-gap center gap8">
                  <div className={css.indicator} data-active={Boolean(props.values[sourceIdToIdMap['MDReadySgl']])} />
                  <p className="text-nm">READY</p>
                  <QuestionIconWithTooltip text="Сигнал READY" position="bottom" />
                </div>

                <div className="inline-flex-gap center gap8">
                  <div className={css.indicator} data-active={Boolean(props.values[sourceIdToIdMap['FCLSCmd']])} />
                  <p className="text-nm">LS</p>
                  <QuestionIconWithTooltip text="Сигнал LS" position="right" />
                </div>
                <div className="inline-flex-gap center gap8">
                  <div className={css.indicator} data-active={Boolean(props.values[sourceIdToIdMap['FCDECCmd']])} />
                  <p className="text-nm">DEC</p>
                  <QuestionIconWithTooltip text="Сигнал DEC" position="bottom" />
                </div>
              </div>
            </div>
          </div>

          {/* Силовая цепь */}
          <div className={css.indicationBlockWrapper}>
            <div className="flex-lines gap24">
              <h5 className="text-heading-5">Cиловая цепь</h5>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '32px' }}>
                <div className="inline-flex-gap center gap8">
                  <div className={css.indicator} data-active={Boolean(props.values[sourceIdToIdMap['LKM1Cmd']])} />
                  <p className="text-nm">LKM1</p>
                  <QuestionIconWithTooltip text="Команда на выход LKM1" position="right" />
                </div>
                <div className="inline-flex-gap center gap8">
                  <div className={css.indicator} data-active={Boolean(props.values[sourceIdToIdMap['ONKMCSgl']])} />
                  <p className="text-nm">ONKMC</p>
                  <QuestionIconWithTooltip text="Сигнал контактора КМС" position="bottom" />
                </div>
                <div className="inline-flex-gap center gap8">
                  <div
                    className={css.indicator}
                    data-active={Boolean(props.values[sourceIdToIdMap['phaseControlRelayState']])}
                  />
                  <p className="text-nm">~380</p>
                  <QuestionIconWithTooltip text="Сигнал реле контроля фаз (380В)" position="bottom" />
                </div>

                <div className="inline-flex-gap center gap8">
                  <div className={css.indicator} data-active={Boolean(props.values[sourceIdToIdMap['LKM2Cmd']])} />
                  <p className="text-nm">LKM2</p>
                  <QuestionIconWithTooltip text="Команда на выход LKM2" position="right" />
                </div>
                <div className="inline-flex-gap center gap8">
                  <div
                    className={css.indicator}
                    data-active={Boolean(props.values[sourceIdToIdMap['KDSRelayState']])}
                  />
                  <p className="text-nm">КДС</p>
                  <QuestionIconWithTooltip text="Сигнал реле КДС (1 открыто / 0 закрыто)" position="bottom" />
                </div>
                <div className="inline-flex-gap center gap8">
                  <div
                    className={css.indicator}
                    data-active={Boolean(props.values[sourceIdToIdMap['safetyChainState']])}
                  />
                  <p className="text-nm">ЦБ</p>
                  <QuestionIconWithTooltip
                    text="Сигнал от схемы контроля напряжения в цепи безопасности 110В"
                    position="bottom"
                  />
                </div>
                <div className="inline-flex-gap center gap8">
                  <div className={css.indicator} data-active={Boolean(props.values[sourceIdToIdMap['LKMCCmd']])} />
                  <p className="text-nm">LKMCC</p>
                  <QuestionIconWithTooltip text="Команда на контактор КМС" position="right" />
                </div>
                <div className="inline-flex-gap center gap8">
                  <div className={css.indicator} data-active={Boolean(props.values[sourceIdToIdMap['K7RelayState']])} />
                  <p className="text-nm">K7</p>
                  <QuestionIconWithTooltip text="Сигнал реле К7 (1 открыто / 0 закрыто)" position="bottom" />
                </div>
              </div>
            </div>
          </div>

          {/* Цепь безопасности */}
          <div className={css.indicationBlockWrapper}>
            <div className="flex-lines gap24">
              <h5 className="text-heading-5">Цепь безопасности</h5>
              <div className="inline-flex-gap gap32">
                <div className="inline-flex-gap center gap8">
                  <div
                    className={css.indicator}
                    data-active={Boolean(props.values[sourceIdToIdMap['RKBRelayState']])}
                  />
                  <p className="text-nm">РКБ</p>
                  <QuestionIconWithTooltip text="Реле контроля безопасности" position="right" />
                </div>
                <div className="inline-flex-gap center gap8">
                  <div
                    className={css.indicator}
                    data-active={Boolean(props.values[sourceIdToIdMap['shaftGuardState']])}
                  />
                  <p className="text-nm">ОШ</p>
                  <QuestionIconWithTooltip text="Охрана шахты" position="top" />
                </div>
                <div className="inline-flex-gap center gap8">
                  <div
                    className={css.indicator}
                    data-active={Boolean(props.values[sourceIdToIdMap['shaftDoorsState']])}
                  />
                  <p className="text-nm">ДШ</p>
                  <QuestionIconWithTooltip text="Двери шахты" position="top" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Двери шахты */}
        <div className={css.indicationBlockWrapper}>
          <div className="flex-lines gap24 width100">
            <h5 className="text-heading-5">Привод дверей</h5>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
              <p className="text-sm">Сигнал / Сторона</p>
              <p className="text-nm">Сторона А</p>
              <p className="text-nm">Сторона Б</p>

              {/* ВКО */}
              <div className="inline-flex-gap gap4">
                <p className="text-nm">ВКО</p>
                <QuestionIconWithTooltip text="Сигналы открытия дверей" position="bottom" />
              </div>
              <div className={css.indicator} data-active={Boolean(props.values[sourceIdToIdMap['VKODoorSideASgl']])} />
              <div className={css.indicator} data-active={Boolean(props.values[sourceIdToIdMap['VKODoorSideBSgl']])} />

              {/* ВКЗ */}
              <div className="inline-flex-gap gap4">
                <p className="text-nm">ВКЗ</p>
                <QuestionIconWithTooltip text="Сигналы закрытия дверей" position="bottom" />
              </div>
              <div className={css.indicator} data-active={Boolean(props.values[sourceIdToIdMap['VKZDoorSideASgl']])} />
              <div className={css.indicator} data-active={Boolean(props.values[sourceIdToIdMap['VKZDoorSideBSgl']])} />

              {/* ВБР */}
              <div className="inline-flex-gap gap4">
                <p className="text-nm">ВБР</p>
                <QuestionIconWithTooltip text="Сигналы реверса дверей" position="bottom" />
              </div>
              <div className={css.indicator} data-active={Boolean(props.values[sourceIdToIdMap['VBRDoorSideASgl']])} />
              <div className={css.indicator} data-active={Boolean(props.values[sourceIdToIdMap['VBRDoorSideBSgl']])} />

              {/* Фотозавеса */}
              <div className="inline-flex-gap gap4">
                <p className="text-nm">ФЗ</p>
                <QuestionIconWithTooltip text="Сигналы датчиков фотозавесы" position="bottom" />
              </div>
              <div
                className={css.indicator}
                data-active={Boolean(props.values[sourceIdToIdMap['doorControlSensorSideASgl']])}
              />
              <div
                className={css.indicator}
                data-active={Boolean(props.values[sourceIdToIdMap['doorControlSensorSideBSgl']])}
              />

              {/* МБМ */}
              <div className="inline-flex-gap gap4">
                <p className="text-nm">МБМ</p>
                <QuestionIconWithTooltip text="Модуль безопасного моста" position="bottom" />
              </div>
              <div
                className={css.indicator}
                data-active={Boolean(props.values[sourceIdToIdMap['MBMInstalledSideAFlag']])}
              />
              <div
                className={css.indicator}
                data-active={Boolean(props.values[sourceIdToIdMap['MBMInstalledSideBFlag']])}
              />

              {/* Блокировка */}
              <div className="inline-flex-gap gap4">
                <p className="text-nm">Блок</p>
                <QuestionIconWithTooltip text="Блокировки привода дверей" position="bottom" />
              </div>
              <div className={css.indicator} data-active={Boolean(props.values[sourceIdToIdMap['blockSideAFlag']])} />
              <div className={css.indicator} data-active={Boolean(props.values[sourceIdToIdMap['blockSideBFlag']])} />

              {/* Команда открыть двери */}
              <div className="inline-flex-gap gap4">
                <p className="text-nm">ОД</p>
                <QuestionIconWithTooltip text="Команды открытия дверей" position="bottom" />
              </div>
              <div className={css.indicator} data-active={Boolean(props.values[sourceIdToIdMap['openDoorSideACmd']])} />
              <div className={css.indicator} data-active={Boolean(props.values[sourceIdToIdMap['openDoorSideBCmd']])} />

              {/* Команда закрыть двери */}
              <div className="inline-flex-gap gap4">
                <p className="text-nm">ЗД</p>
                <QuestionIconWithTooltip text="Команды закрытия дверей" position="bottom" />
              </div>
              <div
                className={css.indicator}
                data-active={Boolean(props.values[sourceIdToIdMap['closeDoorSideACmd']])}
              />
              <div
                className={css.indicator}
                data-active={Boolean(props.values[sourceIdToIdMap['closeDoorSideBCmd']])}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LiftIndicatorsWidget
